// Copyright ©️ 2026 eVolve MEP, LLC

import './modals.css';

import React, { type FormEvent, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  Box,
  Flex,
  LoadingOverlay,
  type MantineColor,
  // oxlint-disable-next-line no-restricted-imports
  Modal,
  type ModalBodyProps,
  type ModalHeaderProps,
  Stack,
  Tabs,
} from '@mantine/core';
import { useMergedRef, useUncontrolled } from '@mantine/hooks';

import { EvolveIcon, type EvolveIconName } from 'assets/icons/EvolveIcon';
import { MODAL_DIVIDER_BORDER } from 'components/Mantine/Modals/helpers';
import { type ModalButtonProps, ModalButtons } from 'components/Mantine/Modals/ModalButtons';
import { useModalOpenGuard } from 'components/Mantine/Modals/useModalOpenGuard';
import { useOnConfirmWrapper } from 'components/Mantine/Modals/useOnConfirmWrapper';
import type { StrongOmit, WithOptional, WithRequired } from 'helpers/helper-types';
import { isNil, isNotNil } from 'helpers/isNotNil';
import { useGeneralContext } from 'helpers/useGeneralContext';
import { useIsMobile } from 'hooks/useIsMobile';
import { DEFAULT_TRANSITIONS_DURATION } from 'styles/theme/useGenerateMantineTheme';

// TODO: What should this be?
export type SubmitFormEvent = FormEvent<HTMLFormElement>;

/** Modal header background, as CSS. Equivalent to the `bg="gray.1"` shorthand `WrappedModal` uses. */
export const MODAL_HEADER_BG = 'var(--mantine-color-gray-1)';
/** Corner radius shared by every modal surface. */
export const MODAL_RADIUS = 8;

export type WrappedModalProps<TTab extends string = string> = {
  /** Component to render to the right of the title. */
  headerRightComponent?: ReactNode;
  /** A mini header to render above the standard header. If provided, the Close button will be moved to the mini header. */
  miniHeader?: ReactNode;
  /** If true, hides the standard header. Pair with `miniHeader` so a Close button still renders. */
  hideHeader?: boolean;
  /**
   * Whether or not to close the modal after `onConfirm` completes
   * @default true
   */
  closeOnConfirm?: boolean;
  /** Used by `FormModal` */
  formSubmit?: (e: SubmitFormEvent) => Promise<void> | void;
  subtitle?: ReactNode;
  titleIcon?: EvolveIconName | null;
  titleIconColor?: MantineColor;
  headerProps?: ModalHeaderProps;
  bodyProps?: ModalBodyProps;
  /** If true, will make the modal taller */
  tall?: boolean;
  alertState?: {
    title?: ReactNode;
    subtitle?: ReactNode;
    message: ReactNode;
    showAlert: boolean;
  };
} & WithRequired<StrongOmit<Modal.Props, 'onClose' | 'onSubmit' | 'children'>, 'title'> &
  (
    | ({
        hideModalButtons?: boolean;
      } & ModalButtonProps)
    | ({
        hideModalButtons: true;
      } & WithRequired<WithOptional<ModalButtonProps, 'onConfirm'>, 'onClose'>)
  ) &
  (
    | {
        tabs: readonly {
          label: ReactNode;
          value: TTab;
          icon?: EvolveIconName | null;
          rightSection?: ReactNode;
          disabled?: boolean;
          hidden?: boolean;
        }[];
        selectedTab?: TTab | null;
        onTabChange?: (tab: TTab | null) => void;
        children?: (selectedTab: TTab) => ReactNode;
      }
    | {
        tabs?: never;
        selectedTab?: never;
        onTabChange?: never;
        children?: ReactNode;
      }
  );

type ModalContextType = {
  /**
   * Used to manage an additional `loading` state of the Confirm button.
   * Will be used in junction with the `loading` prop passed into the Modal, if any.
   */
  setLoading: (loading: boolean) => void;
  /**
   * Used to manage an additional `disabled` state of the Confirm button.
   * Will be used in junction with the `disabled` prop passed into the Modal, if any.
   */
  setDisabled: (disabled: boolean) => void;
  /**
   * Overrides the `confirmationText` prop passed into the Modal.
   */
  setConfirmationText: (overrideText: string | null | undefined) => void;
  /**
   * Overrides the `cancellationText` prop passed into the Modal.
   */
  setCancellationText: (overrideText: string | null | undefined) => void;
  /**
   * Overrides the `onClose` prop passed into the Modal.
   */
  setOnClose: (onClose: ((event?: SubmitFormEvent) => void) | null | undefined) => void;
};
const ModalContext = React.createContext<ModalContextType | undefined>(undefined);
export const useWrappedModal = () => useGeneralContext(ModalContext, 'WrappedModal');

export const OptionalForm = ({
  onFormSubmit,
  children,
  formStyle,
}: {
  onFormSubmit?: (event?: SubmitFormEvent) => void;
  children: ReactNode;
  formStyle?: React.CSSProperties;
}) =>
  onFormSubmit ? (
    <form onSubmit={onFormSubmit} style={{ height: '100%', ...formStyle }}>
      {children}
    </form>
  ) : (
    children
  );

/**
 * Standard Modal.
 *
 * For Modals with forms, use `FormModal`.
 *
 * Contents are not mounted until the modal is opened, so any data fetching belongs in a contents sub-component
 * rather than in the component that renders the modal.
 */
export const WrappedModal = <TTab extends string = string>(baseProps: WrappedModalProps<TTab>) => {
  const isMobile = useIsMobile();
  const { opened, onClose, onConfirm, ...rest } = baseProps;
  const cachedProps = useRef(rest);
  useEffect(() => {
    // Only cache the rest of the props when the modal is opened, excluding `opened`, `onClose`, and `onConfirm`.
    // Otherwise, curRest includes these props and passes them to `Modal.Root` when the modal is not opened, which could cause unintended behavior.
    const { opened: curOpened, onClose: _onClose, onConfirm: _onConfirm, ...curRest } = baseProps;
    if (curOpened) cachedProps.current = curRest;
  }, [baseProps]);

  const {
    miniHeader,
    hideHeader,
    headerRightComponent,
    title,
    withCloseButton = true,
    tabs: unfilteredTabs,
    selectedTab: selectedTabParent,
    onTabChange,
    children,
    formSubmit,
    loading: parentLoading,
    disabled: parentDisabled,
    closeOnConfirm = true,
    hideModalButtons,
    closeButtonProps,
    overlayProps,
    subtitle,
    titleIcon = null,
    titleIconColor,
    headerProps,
    tall,
    bodyProps,
    alertState,
    radius = MODAL_RADIUS,
    styles,
    fullScreen,
    trapFocus = true,
    ...props
  } = opened ? rest : cachedProps.current;

  const tabs = useMemo(() => unfilteredTabs?.filter((f) => !f.hidden), [unfilteredTabs]);

  const confirmButtonRef = useRef<HTMLButtonElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const [internalLoading, setInternalLoading] = useState(false);
  const [internalDisabled, setInternalDisabled] = useState(false);
  const [confirmationTextOverride, setConfirmationTextOverride] = useState<string | undefined | null>(undefined);
  const [cancellationTextOverride, setCancellationTextOverride] = useState<string | undefined | null>(undefined);
  const [onCloseOverride, setOnCloseOverride] = useState<((event?: SubmitFormEvent) => void) | undefined | null>(
    undefined,
  );
  // A `keepMounted` modal's content stays `display: none` (the theme's `keepMountedMode`) until its enter transition
  // starts, a frame after `opened` flips. Mantine's focus trap looks for something to focus as soon as it activates,
  // skips anything inside `display: none` and gives up, so only activate it while the content is actually displayed.
  // Note: If we ever change the `keepMountedMode`, we may need to revisit this logic to ensure the focus trap behaves correctly.
  const [contentDisplayed, setContentDisplayed] = useState(false);
  const observeContentDisplay = useCallback((node: HTMLElement | null) => {
    if (isNil(node)) return undefined;
    const updateContentDisplayed = () => setContentDisplayed(node.style.display !== 'none');
    updateContentDisplayed();
    const observer = new MutationObserver(updateContentDisplayed);
    observer.observe(node, { attributes: true, attributeFilter: ['style'] });
    return () => {
      observer.disconnect();
      setContentDisplayed(false);
    };
  }, []);
  const contentRef = useMergedRef(scrollRef, observeContentDisplay);

  // If our parent component provides a `ModalOpenGuard`, make sure we register ourselves
  const modalOpenGuard = useModalOpenGuard();
  useEffect(() => {
    if (!opened || !modalOpenGuard) return undefined;
    return modalOpenGuard.register();
  }, [opened, modalOpenGuard]);

  const { submitting, onConfirmWrapper } = useOnConfirmWrapper({ onConfirm, formSubmit, closeOnConfirm, onClose });

  const loading = parentLoading || internalLoading || submitting;
  const disabled = parentDisabled || internalDisabled;

  // Exclude any props from `ModalButtons` so they aren't passed to the Modal
  const {
    confirmationText,
    cancellationText,
    confirmationTooltip,
    leftComponent: _1,
    withCancelButton: _2,
    buttonColor: _3,
    type: _4,
    ref: _5,
    transitionProps: consumerTransitionProps,
    ...modalProps
  } = props;

  useEffect(() => {
    // When loading, move focus to the Confirmation button
    // to draw focus away from any input elements
    if (loading) confirmButtonRef.current?.focus();
  }, [loading]);

  const [selectedTab, setSelectedTab] = useUncontrolled<TTab | null>({
    defaultValue: tabs?.[0]?.value ?? null,
    value: selectedTabParent,
    onChange: onTabChange,
  });

  if (alertState?.showAlert) {
    return (
      <WrappedModal
        title={alertState.title ?? title}
        subtitle={alertState.subtitle}
        titleIcon="Alert"
        titleIconColor="red"
        opened={opened}
        onConfirm={onClose ?? onConfirmWrapper}
        withCancelButton={false}
      >
        {alertState.message}
      </WrappedModal>
    );
  }

  return (
    <ModalContext
      value={{
        setLoading: setInternalLoading,
        setDisabled: setInternalDisabled,
        setCancellationText: setCancellationTextOverride,
        setConfirmationText: setConfirmationTextOverride,
        setOnClose: setOnCloseOverride,
      }}
    >
      <Modal.Root
        onClose={onCloseOverride ?? onClose ?? onConfirmWrapper}
        opened={opened}
        onClick={(e) => e.stopPropagation()}
        radius={radius}
        styles={(...args) => {
          const base = typeof styles === 'function' ? styles(...args) : styles;
          return {
            ...base,
            inner:
              fullScreen && !isMobile
                ? {
                    position: 'fixed',
                    left: 0,
                    right: 0,
                    top: 0,
                    bottom: 0,
                    paddingLeft: '2.1rem',
                    paddingRight: '2.1rem',
                    paddingTop: '2rem',
                    paddingBottom: '2rem',
                    ...base?.inner,
                  }
                : base?.inner,
            content: fullScreen
              ? {
                  flex: 1,
                  height: '100%',
                  maxHeight: 'unset',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                  ...base?.content,
                }
              : base?.content,
          };
        }}
        transitionProps={{
          /** Under the hood, mantine's `ModalBaseContent` has a `Transition` with the transition prop as "pop"
           *  `ModalRoot` overrides this with a default prop, but since `transitionProps` is an object,
           *  it would get clobbered here if we don't restate it.
           *  This is important for sizing some components like `SegmentedControl`.
           *  "pop" shrinks things at first, "fade-down" doesn't
           **/
          transition: isMobile && fullScreen ? 'fade' : 'fade-down',
          duration: DEFAULT_TRANSITIONS_DURATION,
          ...consumerTransitionProps,
        }}
        fullScreen={fullScreen && isMobile}
        {...modalProps}
        trapFocus={trapFocus && contentDisplayed}
      >
        <Modal.Overlay {...overlayProps} />
        <Modal.Content radius={radius} ref={contentRef}>
          {(!hideHeader || isNotNil(miniHeader)) && (
            <Modal.Header
              bg={MODAL_HEADER_BG}
              display="flex"
              p={0}
              {...headerProps}
              style={{
                // Mantine's Modal.Header enforces a 60px min-height unconditionally (not exposed as an
                // overridable CSS var like padding is), so the mini-only header must unset it explicitly.
                ...(hideHeader ? { minHeight: 0 } : undefined),
                // TODO: `.style` can now be a function, need to handle it
                // oxlint-disable-next-line typescript/no-misused-spread
                ...headerProps?.style,
              }}
            >
              <Stack flex={1} gap={0} mih={hideHeader ? undefined : 60}>
                {isNotNil(miniHeader) && (
                  <Flex
                    justify="space-between"
                    align="center"
                    gap="md"
                    pl="md"
                    pr="xs"
                    pt={3}
                    pb={2}
                    bg="gray.1"
                    style={{
                      borderBottom: hideHeader ? undefined : MODAL_DIVIDER_BORDER,
                    }}
                  >
                    <Flex align="center" c="gray" fz="sm" flex={1}>
                      {miniHeader}
                    </Flex>
                    {withCloseButton && <Modal.CloseButton {...closeButtonProps} disabled={loading} />}
                  </Flex>
                )}
                {!hideHeader && (
                  <>
                    <Flex
                      p="md"
                      pr={withCloseButton && isNil(miniHeader) ? 44 : 'md'}
                      justify="space-between"
                      align="center"
                      flex={1}
                      gap="md"
                    >
                      <Stack gap={4} w="100%">
                        <Flex align="center" gap="xs" w="100%">
                          <EvolveIcon icon={titleIcon} color={titleIconColor} />
                          {typeof title === 'string' ? (
                            <Modal.Title fw={500} style={{ wordBreak: 'break-word' }}>
                              {title}
                            </Modal.Title>
                          ) : (
                            title
                          )}
                        </Flex>
                        {subtitle && (
                          <Flex fz="sm" c="dimmed">
                            {subtitle}
                          </Flex>
                        )}
                      </Stack>
                      {isNotNil(headerRightComponent) && (
                        <Flex align="center" gap="md" flex="0 0 auto">
                          {headerRightComponent}
                        </Flex>
                      )}
                    </Flex>
                    <Tabs value={selectedTab} onChange={(v) => setSelectedTab(v as TTab)} className="modal-tabs">
                      <Tabs.List>
                        {tabs?.map((tab) => (
                          <Tabs.Tab
                            key={tab.value}
                            value={tab.value}
                            leftSection={<EvolveIcon icon={tab.icon ?? null} size="sm" />}
                            rightSection={tab.rightSection ?? null}
                            disabled={tab.disabled}
                          >
                            {tab.label}
                          </Tabs.Tab>
                        ))}
                      </Tabs.List>
                    </Tabs>
                  </>
                )}
              </Stack>
              {withCloseButton && !hideHeader && !miniHeader && (
                <Box pos="absolute" right={8}>
                  <Modal.CloseButton {...closeButtonProps} disabled={loading} />
                </Box>
              )}
            </Modal.Header>
          )}

          <OptionalForm
            onFormSubmit={formSubmit ? onConfirmWrapper : undefined}
            formStyle={
              fullScreen
                ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', height: 'unset' }
                : undefined
            }
          >
            <Modal.Body
              p="md"
              mih={
                tall ? 'min(750px, calc(100vh - 144px - (2 * var(--modal-inner-y-offset, var(--modal-y-offset)))))' : 50
              }
              h={
                tall
                  ? 'min(750px, calc(100vh - 144px - (2 * var(--modal-inner-y-offset, var(--modal-y-offset)))))'
                  : undefined
              }
              {...bodyProps}
              style={{
                // Contain over-wide children (fixed-width rows, unwrapped flexes) by scrolling
                // them within the body, so the modal never appears to extend past the viewport —
                // most visible on phones with display scaling turned up.
                overflowX: isMobile ? 'auto' : undefined,
                ...(fullScreen
                  ? { flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }
                  : undefined),
                // TODO: `.style` can now be a function, need to handle it
                // oxlint-disable-next-line typescript/no-misused-spread
                ...bodyProps?.style,
              }}
            >
              <Stack flex="1 0 0" h="100%" w="100%" maw="100%">
                {unfilteredTabs ? children?.(selectedTab as TTab) : children}
              </Stack>
            </Modal.Body>

            {!hideModalButtons && (
              <ModalButtons
                ref={confirmButtonRef}
                {...props}
                confirmationText={confirmationTextOverride ?? confirmationText}
                cancellationText={cancellationTextOverride ?? cancellationText}
                confirmationTooltip={confirmationTooltip}
                onClose={onCloseOverride ?? onClose ?? onConfirmWrapper}
                onConfirm={onConfirmWrapper}
                disabled={disabled}
              />
            )}
          </OptionalForm>

          <LoadingOverlay
            visible={loading}
            style={{
              marginTop: scrollRef.current?.scrollTop ?? 0,
              marginBottom: -(scrollRef.current?.scrollTop ?? 0),
            }}
          />
        </Modal.Content>
      </Modal.Root>
    </ModalContext>
  );
};
